// CSV parsing - simplified version without papaparse
// For production, install papaparse: npm install papaparse @types/papaparse
import * as z from "zod";
import { detectDialect, applyDialect, normalizeHeader } from "./csvDialects";
import { stripBom, detectSeparator, splitLine } from "./quantowerCsv";

export interface CSVTradeRow {
  date: string;
  symbol: string;
  type: string;
  status?: string;
  entryPrice: string;
  exitPrice?: string;
  exitDate?: string;
  quantity: string;
  pnl: string;
  commission?: string;
  swaps?: string;
  notes?: string;
}

const csvRowSchema = z.object({
  date: z.string().min(1, "Le champ 'date' est obligatoire"),
  symbol: z.string().min(1, "Le champ 'symbol' est obligatoire"),
  type: z.enum(["long", "short", "LONG", "SHORT"], {
    errorMap: () => ({ message: "Le champ 'type' doit être 'long' ou 'short'" }),
  }),
  // `status` est optionnel (défaut "closed" plus bas). La normalisation des
  // lignes produit "" quand la colonne est absente, et une chaîne vide ne
  // satisfait pas l'enum : un CSV natif sans colonne `status` était donc
  // rejeté. On traite le vide comme "non renseigné".
  status: z
    .preprocess(
      (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
      z.enum(["open", "closed", "OPEN", "CLOSED"]).optional(),
    )
    .optional(),
  entryPrice: z.string().min(1, "Le champ 'entryPrice' est obligatoire"),
  exitPrice: z.string().optional(),
  exitDate: z.string().optional(),
  quantity: z.string().min(1, "Le champ 'quantity' est obligatoire"),
  pnl: z.string().optional(), // Optional, especially for open trades
  commission: z.string().optional(),
  swaps: z.string().optional(),
  notes: z.string().optional(),
});

export interface ParsedTrade {
  date: Date;
  symbol: string;
  type: "long" | "short";
  status: "open" | "closed";
  entryPrice: number;
  exitPrice?: number | null;
  exitDate?: Date | null;
  quantity: number;
  pnl: number | null;
  commission: number;
  swaps: number | null;
  notes?: string | null;
  customFields?: Array<{ fieldId: string; value: any }>;
}

export interface CSVImportResult {
  valid: ParsedTrade[];
  errors: Array<{ row: number; error: string }>;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if ((char === "," || char === ";") && !inQuotes) {
      // Support both comma and semicolon as separators
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  // Push the last field
  result.push(current.trim());
  
  return result;
}

/**
 * Parse a date string that may include time information
 * Supports formats:
 * - YYYY-MM-DD
 * - YYYY-MM-DD HH:MM
 * - YYYY-MM-DD HH:MM:SS
 * - YYYY-MM-DDTHH:MM:SS (ISO 8601)
 * If only date is provided, sets time to 00:00:00
 */
function parseDateTime(dateString: string): Date {
  if (!dateString || dateString.trim() === "") {
    throw new Error("Date string is empty");
  }

  // Try ISO 8601 format first (YYYY-MM-DDTHH:MM:SS)
  if (dateString.includes("T")) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Try format with space separator (YYYY-MM-DD HH:MM:SS or YYYY-MM-DD HH:MM)
  if (dateString.includes(" ")) {
    const date = new Date(dateString.replace(" ", "T"));
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Try simple date format (YYYY-MM-DD) - set to midnight
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    // If it's just a date, ensure time is set to 00:00:00
    date.setHours(0, 0, 0, 0);
    return date;
  }

  throw new Error(`Invalid date format: ${dateString}`);
}

/**
 * Convertit une ligne validée en ParsedTrade.
 *
 * Extrait sans changement de comportement du corps de parseCSVFile, pour que
 * l'import natif et les dialectes tiers partagent exactement les mêmes
 * contrôles (prix > 0, quantité > 0, dates, signes).
 */
function buildParsedTrade(
  validatedRow: z.infer<typeof csvRowSchema>,
): ParsedTrade {
  const date = parseDateTime(validatedRow.date);

  // Parse status (default to "closed" if not provided)
  const status = validatedRow.status
    ? (validatedRow.status.toLowerCase() as "open" | "closed")
    : "closed";

  if (status !== "open" && status !== "closed") {
    throw new Error(`Invalid status: ${validatedRow.status}. Must be 'open' or 'closed'`);
  }

  const entryPrice = parseFloat(validatedRow.entryPrice);
  if (isNaN(entryPrice) || entryPrice <= 0) {
    throw new Error(`Invalid entry price: ${validatedRow.entryPrice}`);
  }

  const exitPrice = validatedRow.exitPrice ? parseFloat(validatedRow.exitPrice) : null;
  if (validatedRow.exitPrice && (isNaN(exitPrice!) || exitPrice! <= 0)) {
    throw new Error(`Invalid exit price: ${validatedRow.exitPrice}`);
  }

  const quantity = parseFloat(validatedRow.quantity);
  if (isNaN(quantity) || quantity <= 0) {
    throw new Error(`Invalid quantity: ${validatedRow.quantity}`);
  }

  // PnL is optional for open trades, required for closed trades
  let pnl: number | null = null;
  if (validatedRow.pnl && validatedRow.pnl.trim() !== "") {
    const parsedPnl = parseFloat(validatedRow.pnl);
    if (isNaN(parsedPnl)) {
      throw new Error(`Invalid P&L: ${validatedRow.pnl}`);
    }
    pnl = parsedPnl;
  }

  const commission = validatedRow.commission ? parseFloat(validatedRow.commission) : 0;
  if (validatedRow.commission && isNaN(commission)) {
    throw new Error(`Invalid commission: ${validatedRow.commission}`);
  }
  // Les commissions peuvent être négatives (remises, ajustements, etc.)

  const swaps = validatedRow.swaps ? parseFloat(validatedRow.swaps) : null;
  if (validatedRow.swaps && isNaN(swaps!)) {
    throw new Error(`Invalid swaps: ${validatedRow.swaps}`);
  }
  // Les swaps peuvent être positifs ou négatifs

  const type = validatedRow.type.toLowerCase() as "long" | "short";
  if (type !== "long" && type !== "short") {
    throw new Error(`Invalid type: ${validatedRow.type}. Must be 'long' or 'short'`);
  }

  // Parse exitDate if provided
  let exitDate: Date | null = null;
  if (validatedRow.exitDate && validatedRow.exitDate.trim() !== "") {
    try {
      exitDate = parseDateTime(validatedRow.exitDate);
    } catch (error: any) {
      throw new Error(`Invalid exit date: ${validatedRow.exitDate}. ${error.message}`);
    }
  }

  return {
    date,
    symbol: validatedRow.symbol.toUpperCase().trim(),
    type,
    status,
    entryPrice,
    exitPrice,
    exitDate,
    quantity,
    pnl,
    commission,
    swaps,
    notes: validatedRow.notes?.trim() || null,
  };
}

export function parseCSVFile(
  file: File,
): Promise<CSVImportResult> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Les exports Quantower portent un BOM UTF-8 : sans strip, le premier
        // en-tête se lit "<BOM>Account" et aucun dialecte ne matche.
        const text = stripBom((e.target?.result as string) ?? "");
        const lines = text
          .split(/\r?\n/)
          .filter((line) => line.trim() !== "");

        if (lines.length < 2) {
          resolve({
            valid: [],
            errors: [{ row: 0, error: "CSV file must have at least a header and one data row" }],
          });
          return;
        }

        // Quantower laisse choisir le séparateur à l'export (virgule,
        // point-virgule, tabulation, espace). parseCSVLine ne connaît que les
        // deux premiers : on découpe l'en-tête avec le séparateur réel pour ne
        // pas rater un dialecte à cause du format du fichier.
        const separator = detectSeparator(lines[0]);
        const headers = splitLine(lines[0], separator);
        const normalizedHeaders = headers.map((h) => h.trim().toLowerCase());

        // Export d'une plateforme tierce (TopstepX...) : les colonnes portent
        // d'autres noms mais le contenu est complet. On traduit au lieu de
        // rejeter — sans quoi l'utilisateur doit renommer ses colonnes à la main.
        const dialect = detectDialect(headers);

        // Debug: vérifier que tous les en-têtes requis sont présents
        const requiredHeaders = ["date", "symbol", "type", "entryprice", "quantity"];
        const missingHeaders = dialect
          ? []
          : requiredHeaders.filter((req) => !normalizedHeaders.includes(req));

        if (missingHeaders.length > 0) {
          resolve({
            valid: [],
            errors: [
              {
                row: 0,
                // Nommer les formats acceptés : sans cela, l'utilisateur d'un
                // export non reconnu ne sait pas si son fichier est en cause ou
                // si l'app ne gère tout simplement pas sa plateforme.
                error: `Format de CSV non reconnu. Formats acceptés : edgely, Tradovate, TopstepX, Quantower. En-têtes manquants pour le format edgely : ${missingHeaders.join(", ")}. En-têtes détectés : ${normalizedHeaders.join(", ")}`,
              },
            ],
          });
          return;
        }

        const valid: ParsedTrade[] = [];
        const errors: Array<{ row: number; error: string }> = [];

        // Dialecte fichier-entier (Quantower) : un trade se déduit de plusieurs
        // lignes, la traduction voit donc tout le fichier d'un coup. Le reste
        // du pipeline (validation, contrôles, ParsedTrade) reste commun.
        if (dialect?.mapFile) {
          const rawRows: Record<string, string>[] = [];
          for (let i = 1; i < lines.length; i++) {
            const values = splitLine(lines[i], separator);
            const rawRow: Record<string, string> = {};
            headers.forEach((header, index) => {
              rawRow[normalizeHeader(header)] = values[index]?.trim() || "";
            });
            rawRows.push(rawRow);
          }

          const mapped = dialect.mapFile(rawRows);
          errors.push(...mapped.errors);

          mapped.rows.forEach((nativeRow, index) => {
            try {
              valid.push(buildParsedTrade(csvRowSchema.parse(nativeRow)));
            } catch (error: any) {
              const message =
                error instanceof z.ZodError
                  ? error.errors
                      .map((e) => `${e.path.join(".")}: ${e.message}`)
                      .join(", ")
                  : error.message;
              errors.push({
                row: index + 1,
                error: `Erreurs de validation (${dialect.label}) - ${message}`,
              });
            }
          });

          resolve({ valid, errors });
          return;
        }

        for (let i = 1; i < lines.length; i++) {
          let values: string[] = [];
          try {
            values = parseCSVLine(lines[i]);
            
            // Si le nombre de colonnes ne correspond pas, remplir avec des valeurs vides
            // Cela gère les cas où des colonnes vides à la fin ne sont pas présentes
            if (values.length < headers.length) {
              // Ajouter des colonnes vides pour correspondre au nombre d'en-têtes
              while (values.length < headers.length) {
                values.push("");
              }
            } else if (values.length > headers.length) {
              // Si trop de colonnes, tronquer (ne devrait pas arriver normalement)
              values = values.slice(0, headers.length);
            }
            
            const row: any = {};
            headers.forEach((header, index) => {
              const normalizedHeader = header.trim().toLowerCase();
              // Normaliser aussi les variations comme "entryPrice" -> "entryprice"
              const value = values[index]?.trim() || "";
              row[normalizedHeader] = value;
              // Garder aussi la version originale pour compatibilité
              row[header.trim()] = value;
            });

            // Debug: afficher les valeurs détectées pour les champs obligatoires
            const debugInfo: string[] = [];
            if (!row.date || row.date === "") debugInfo.push(`date='${row.date}'`);
            if (!row.symbol || row.symbol === "") debugInfo.push(`symbol='${row.symbol}'`);
            if (!row.type || row.type === "") debugInfo.push(`type='${row.type}'`);
            if (!row.entryprice || row.entryprice === "") debugInfo.push(`entryPrice='${row.entryprice}'`);
            if (!row.quantity || row.quantity === "") debugInfo.push(`quantity='${row.quantity}'`);
            
            // Export tiers : on traduit vers le format natif avant validation,
            // le reste du pipeline (parsing, contrôles, ParsedTrade) est commun.
            if (dialect) {
              const dialectRow: Record<string, string> = {};
              headers.forEach((header, index) => {
                dialectRow[normalizeHeader(header)] = values[index]?.trim() || "";
              });
              const mapped = applyDialect(dialect, dialectRow);

              let validatedDialectRow;
              try {
                validatedDialectRow = csvRowSchema.parse(mapped);
              } catch (error: any) {
                if (error instanceof z.ZodError) {
                  const errorMessages = error.errors.map(
                    (e) => `${e.path.join(".")}: ${e.message}`,
                  );
                  throw new Error(
                    `Erreurs de validation (${dialect.label}) - ${errorMessages.join(", ")}`,
                  );
                }
                throw error;
              }
              valid.push(buildParsedTrade(validatedDialectRow));
              continue;
            }

            // Normaliser les noms de colonnes (gérer les variations comme entryPrice vs entryprice)
            const normalizedRow: any = {
              date: row.date || row.Date || "",
              symbol: row.symbol || row.Symbol || "",
              type: row.type || row.Type || "",
              status: row.status || row.Status || "",
              entryPrice: row.entryprice || row.entryPrice || row.EntryPrice || "",
              exitPrice: row.exitprice || row.exitPrice || row.ExitPrice || "",
              exitDate: row.exitdate || row.exitDate || row.ExitDate || "",
              quantity: row.quantity || row.Quantity || "",
              pnl: row.pnl || row.PnL || row.PNL || "",
              commission: row.commission || row.Commission || "",
              swaps: row.swaps || row.Swaps || "",
              notes: row.notes || row.Notes || "",
            };

            // Validate row structure
            let validatedRow;
            try {
              validatedRow = csvRowSchema.parse(normalizedRow);
            } catch (error: any) {
              if (error instanceof z.ZodError) {
                const errorMessages = error.errors.map((e) => {
                  const field = e.path.join(".");
                  return `${field}: ${e.message}`;
                });
                const debugMsg = debugInfo.length > 0 
                  ? ` (Valeurs détectées: ${debugInfo.join(", ")})`
                  : "";
                throw new Error(`Erreurs de validation - ${errorMessages.join(", ")}${debugMsg}`);
              }
              throw error;
            }

            valid.push(buildParsedTrade(validatedRow));
            } catch (error: any) {
              // Ajouter des informations de débogage pour aider à identifier le problème
              const errorMessage = error.message || "Unknown error";
              const debugDetails = ` (En-têtes: ${normalizedHeaders.join(", ")}, Colonnes détectées: ${values.length})`;
              errors.push({
                row: i + 1,
                error: errorMessage + debugDetails,
              });
            }
        }

        resolve({ valid, errors });
      } catch (error: any) {
        resolve({
          valid: [],
          errors: [{ row: 0, error: `CSV parsing error: ${error.message}` }],
        });
      }
    };
    reader.onerror = () => {
      resolve({
        valid: [],
        errors: [{ row: 0, error: "Error reading file" }],
      });
    };
    reader.readAsText(file);
  });
}

export const CSV_HEADERS = [
  "date",
  "symbol",
  "type",
  "status",
  "entryPrice",
  "exitPrice",
  "exitDate",
  "quantity",
  "pnl",
  "commission",
  "swaps",
  "notes",
] as const;

export function exportTradesToCSV(trades: any[]): string {
  // Create CSV header
  const header = CSV_HEADERS.join(",");
  
  // Helper function to format date with time in ISO 8601 format
  const formatDateTime = (date: Date | string | null | undefined): string => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "";
    // Format as ISO 8601: YYYY-MM-DDTHH:MM:SS
    return d.toISOString().replace("Z", "").replace(/\.\d{3}$/, "");
  };
  
  // Create CSV rows
  const rows = trades.map((trade) => {
    const date = formatDateTime(trade.date);
    const exitDate = formatDateTime(trade.exitDate);
    const values = [
      date,
      trade.symbol,
      trade.type,
      trade.status || "closed",
      trade.entryPrice.toString(),
      trade.exitPrice?.toString() || "",
      exitDate,
      trade.quantity.toString(),
      trade.pnl?.toString() || "",
      trade.commission?.toString() || "0",
      trade.swaps?.toString() || "",
      trade.notes ? `"${trade.notes.replace(/"/g, '""')}"` : "",
    ];
    return values.join(",");
  });

  return [header, ...rows].join("\n");
}

